import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import { Button } from '@bibliocommons/base-buttons';
import { Dropdown, DropdownMenuItem, DropdownMenuDivider } from '@bibliocommons/base-dropdown';
import ToggleSwitch from '@bibliocommons/toggle-switch';
import { useRouter } from '@bibliocommons/utils-routing';
import AnalyticsConstants from '@bibliocommons/constants-analytics';
import { sendAnalyticsEvents } from '@bibliocommons/utils-analytics';
import Context from '@bibliocommons/context';
import { composeEvent, composeAnalytics } from 'app/helpers/analytics/compose';

import './EventsViewOptions.scss';

const { EVENTS, CATEGORIES, ACTIONS, LABELS, CUSTOM_DIMENSIONS } = AnalyticsConstants;

export default function EventsViewOptions({ eventsRSSLink, hasFeaturedEvents, hideFeaturedEventsToggle }) {
  const router = useRouter();
  const { mobileApp } = useContext(Context);
  const isFeaturedToggleActive = router.query.featured === 'true';
  const isCancelledToggleActive = router.query.cancelled === 'false';

  function renderFeaturedToggle() {
    function handleFeaturedToggle() {
      sendAnalyticsEvents(
        composeAnalytics([
          composeEvent(EVENTS.TOGGLE_FEATURED_EVENTS, {
            gtmCategory: CATEGORIES.EVENTS_FILTER,
            gtmAction: ACTIONS.TOGGLE_FEATURED_EVENTS_ONLY,
            gtmLabel: isFeaturedToggleActive ? LABELS.OFF : LABELS.ON,
            gtmValue: 1,
            gtmContainerName: CUSTOM_DIMENSIONS.CONTAINER_NAMES.VIEW_OPTIONS
          })
        ])
      );

      router.push({
        query: {
          ...router.query,
          page: undefined,
          featured: isFeaturedToggleActive ? undefined : 'true'
        }
      });
    }

    if (hasFeaturedEvents) {
      return (
        <ToggleSwitch
          id="feature-events-toggle"
          className="feature-events-toggle"
          active={isFeaturedToggleActive}
          activeText={<FormattedMessage id="featured_events_only" />}
          inactiveText={<FormattedMessage id="featured_events_only" />}
          handleClick={handleFeaturedToggle}
        />
      );
    }
    return null;
  }

  function renderAdditionalOptions() {
    function handleCancelledToggle() {
      sendAnalyticsEvents(
        composeAnalytics([
          composeEvent(EVENTS.TOGGLE_CANCELLED_EVENTS, {
            gtmCategory: CATEGORIES.EVENTS_FILTER,
            gtmAction: ACTIONS.TOGGLE_HIDE_CANCELLED_EVENTS,
            gtmLabel: isCancelledToggleActive ? LABELS.OFF : LABELS.ON,
            gtmValue: 1,
            gtmContainerName: CUSTOM_DIMENSIONS.CONTAINER_NAMES.VIEW_OPTIONS
          })
        ])
      );

      router.push({
        query: {
          ...router.query,
          page: undefined,
          cancelled: isCancelledToggleActive ? undefined : 'false'
        }
      });
    }

    function handlePrint() {
      sendAnalyticsEvents(
        composeAnalytics([
          composeEvent(EVENTS.PRINT_CLICK, {
            gtmCategory: CATEGORIES.PAGE_INTERACTIONS,
            gtmAction: ACTIONS.CLICK_LINK,
            gtmLabel: LABELS.PRINT,
            gtmValue: 1,
            gtmContainerName: CUSTOM_DIMENSIONS.CONTAINER_NAMES.VIEW_OPTIONS
          })
        ])
      );

      window.print();
    }

    function handleRSSFeed() {
      const link = eventsRSSLink.replace(/&$/, '');
      window.open(link, '_blank').focus();
    }

    function renderLabel() {
      return <FormattedMessage id="options" />;
    }

    return (
      <Dropdown
        buttonTag={Button}
        dataKey="events-view-options"
        buttonClassName="dropdown-button"
        renderLabel={renderLabel}
        right
      >
        {!hideFeaturedEventsToggle && (
          <>
            <DropdownMenuItem closeOnClick={false}>
              <ToggleSwitch
                id="cancelled-events-toggle"
                active={isCancelledToggleActive}
                handleClick={handleCancelledToggle}
                activeText={<FormattedMessage id="hide_cancelled_events" />}
                inactiveText={<FormattedMessage id="hide_cancelled_events" />}
              />
            </DropdownMenuItem>
            <DropdownMenuDivider />
          </>
        )}
        {!mobileApp ? (
          <DropdownMenuItem
            item={Immutable.Map({ label: <FormattedMessage id="print" />, value: 'print' })}
            handleClick={handlePrint}
          />
        ) : null}
        {eventsRSSLink && (
          <DropdownMenuItem
            item={Immutable.Map({ label: <FormattedMessage id="rss_feed" />, value: 'rss_feed' })}
            handleClick={handleRSSFeed}
          />
        )}
      </Dropdown>
    );
  }

  return (
    <div className="cp-events-view-options">
      {renderFeaturedToggle()}
      {renderAdditionalOptions()}
    </div>
  );
}

EventsViewOptions.propTypes = {
  eventsRSSLink: PropTypes.string,
  hasFeaturedEvents: PropTypes.bool,
  hideFeaturedEventsToggle: PropTypes.bool
};
